import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  getGetMicropaymentMicropaymentsIdGetQueryKey,
  getGetStipendDetailsRouteStipendsStipendIdGetQueryKey,
  MicropaymentActionType,
  StipendActionType,
  useActionMicropaymentMicropaymentsIdActionPost,
  useActionStipendStipendsStipendIdActionPost,
} from "@/api-v3";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { useToast } from "@/hooks/use-toast";
import { AppRoute } from "@/types/routes";

import type { PaymentKind } from "../types";

type ActionablePaymentKind = Extract<PaymentKind, "micropayment" | "stipend">;

interface UsePaymentActionOptions {
  paymentKind: ActionablePaymentKind;
  id: string;
}

export interface PaymentActionHandlers {
  onAccept: () => void;
  onReject: (reason: string) => void;
  isPending: boolean;
}

export const usePaymentAction = ({
  paymentKind,
  id,
}: UsePaymentActionOptions): PaymentActionHandlers => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleMutationError } = useErrorHandler();

  const handleSuccess = (variant: "accept" | "reject") => {
    // removeQueries (not invalidate) so revisiting the page refetches from scratch
    // rather than briefly showing the cached pending state with the action banner.
    const detailKey =
      paymentKind === "micropayment"
        ? getGetMicropaymentMicropaymentsIdGetQueryKey(id)
        : getGetStipendDetailsRouteStipendsStipendIdGetQueryKey(id);
    queryClient.removeQueries({ queryKey: detailKey });
    queryClient.removeQueries({
      predicate: (query) => {
        const root = query.queryKey[0];
        const url = Array.isArray(root) ? root[0] : root;
        return typeof url === "string" && url.startsWith("/payments");
      },
    });

    const descriptionKey =
      paymentKind === "micropayment"
        ? variant === "accept"
          ? "claim_micropayment_approvedToastMessage"
          : "claim_micropayment_rejectedToastMessage"
        : variant === "accept"
          ? "claim_stipend_approvedToastMessage"
          : "claim_stipend_rejectedToastMessage";
    toast({ description: t(descriptionKey) });
    navigate(AppRoute.PAYMENTS);
  };

  const micropaymentMutation = useActionMicropaymentMicropaymentsIdActionPost({
    mutation: {
      onError: handleMutationError,
    },
  });
  const stipendMutation = useActionStipendStipendsStipendIdActionPost({
    mutation: {
      onError: handleMutationError,
    },
  });

  const isPending = micropaymentMutation.isPending || stipendMutation.isPending;

  const dispatch = (variant: "accept" | "reject", externalNotes?: string) => {
    if (paymentKind === "micropayment") {
      micropaymentMutation.mutate(
        {
          id,
          data: {
            action:
              variant === "accept"
                ? MicropaymentActionType.accept
                : MicropaymentActionType.reject,
            external_notes: externalNotes,
          },
        },
        { onSuccess: () => handleSuccess(variant) }
      );
      return;
    }

    stipendMutation.mutate(
      {
        stipendId: id,
        data: {
          action:
            variant === "accept"
              ? StipendActionType.accept
              : StipendActionType.reject,
          external_notes: externalNotes,
        },
      },
      { onSuccess: () => handleSuccess(variant) }
    );
  };

  return {
    onAccept: () => dispatch("accept"),
    onReject: (reason: string) => dispatch("reject", reason),
    isPending,
  };
};
